import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

export class HtxStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  depthConfig: { channel: string; precision: number } | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toLowerCase()}${quote.toLowerCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://api.huobi.pro/v1/common/symbols');

      const res = await proxyAxios.get<{
        data: {
          symbol: string;
          'price-precision': number;
        }[];
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      let prec = 1;

      if (res.data) {
        const find = res.data.data.find(item => item.symbol === this.pair);

        prec = find?.['price-precision'] ?? 1;
      }

      this.precision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Htx ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.htx];

    try {
      const url = new URL('https://api.huobi.pro/market/history/kline');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}${quote.toLowerCase()}`,
        period: innerInterval,
        size: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data: {
          id: number;
          open: number;
          close: number;
          low: number;
          high: number;
          vol: number;
        }[];
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      return (result.data.data ?? []).map(el => [
        el.id * 1000,
        normalizeNumber(el.open),
        normalizeNumber(el.high),
        normalizeNumber(el.low),
        normalizeNumber(el.close),
        normalizeNumber(el.vol),
      ]) as KLine[];
    } catch (error) {
      console.log('Htx klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.huobi.pro/market/history/trade');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}${quote.toLowerCase()}`,
        size: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data: {
          data: {
            amount: string;
            direction: string;
            price: string;
            ts: string;
          }[];
        }[];
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      return (
        (result.data.data ?? []).map(item => ({
          price: item.data[0]?.price,
          quantity: item.data[0]?.amount,
          timestamp: item.data[0]?.ts,
          side: item.data[0]?.direction,
        })) ?? []
      );
    } catch (error) {
      console.log('Htx trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();

    const intervalChannel = {
      60000: '1min',
      300000: '5min',
      900000: '15min',
      3600000: '60min',
      14400000: '4hour',
      86400000: '1dday',
      604800000: '1week',
    }[interval];

    const ws = new ReconnectingWebSocket('wss://api.huobi.pro/ws', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    const id = Date.now().toString();
    const tickerChannel = `market.${this.pair}.ticker`;
    const klineChannel = `market.${this.pair}.kline.${intervalChannel}`;
    const depthChannel = `market.${this.pair}.depth.step0`;
    const tradesChannel = `market.${this.pair}.trade.detail`;

    this.depthConfig =
      this.depthConfig === undefined
        ? {
            channel: depthChannel,
            precision: this.precision,
          }
        : this.depthConfig;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          id: id,
          sub: [klineChannel, tickerChannel, this.depthConfig!.channel, tradesChannel],
        }),
      );
    };

    ws.onmessage = async event => {
      try {
        let rawData;
        if (event.data instanceof Blob) {
          rawData = await event.data.arrayBuffer();
        } else {
          rawData = event.data;
        }

        const data = JSON.parse(pako.ungzip(new Uint8Array(rawData), { to: 'string' }));

        if (data.ch === tickerChannel) {
          const { lastPrice, high, low, vol, open, amount } = data.tick;

          const change = normalizeNumber(((Number(lastPrice) - Number(open)) / Number(open)) * 100);

          this.onTickerUpdate({
            baseVolume: normalizeNumber(amount),
            change: change,
            close: normalizeNumber(lastPrice),
            high: normalizeNumber(high),
            last: lastPrice.toFixed(this.precision),
            low: normalizeNumber(low),
            quoteVolume: normalizeNumber(vol),
          });
        }

        if (data.ch === klineChannel) {
          const { id, open, high, low, close, vol } = data.tick;

          this.klineUpdate([
            id,
            normalizeNumber(open),
            normalizeNumber(high),
            normalizeNumber(low),
            normalizeNumber(close),
            normalizeNumber(vol),
          ]);
        }

        if (data.ch === this.depthConfig!.channel) {
          const { asks, bids } = data.tick as {
            asks: [number, number][];
            bids: [number, number][];
          };
          const precision = this.depthConfig ? this.depthConfig.precision : this.precision;

          const bestAsks = asks.map(el => [el[0].toFixed(precision), normalizeNumber(el[1])]) as [
            string,
            string,
          ][];
          const bestBids = bids.map(el => [el[0].toFixed(precision), normalizeNumber(el[1])]) as [
            string,
            string,
          ][];

          this.bids = bestBids;
          this.asks = bestAsks;

          this.onOrderbookUpdate({
            bids: bestBids,
            asks: bestAsks,
          });
        }

        if (data.ping) {
          this.ws?.send(JSON.stringify({ pong: data.ping }));
        }

        if (data.ch === tradesChannel) {
          if (data.tick?.data?.length) {
            const { price, amount, ts, direction } = data.tick.data[0];

            this.onTradeUpdate({
              price,
              quantity: amount,
              timestamp: ts,
              side: direction,
            });
          }
        }
      } catch (error) {
        console.log('Htx socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({
          unsub: this.depthConfig.channel,
        }),
      );

      this.depthConfig = {
        channel: `market.${this.pair}.depth.step${this.precision - tick}`,
        precision: tick,
      };

      this.ws.send(
        JSON.stringify({
          sub: this.depthConfig.channel,
        }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new HtxStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
